<template>
  <div>
    <section class="relative h-screen w-full">
      <div class="absolute top-0 inset-x-0 bg-gradient-to-b from-black/50 h-225 z-10" />
      <Media
        v-if="data?.entry?.image.length"
        :media="data.entry.image[0]"
        sizes="(max-width: 600px) 100px, 100vw"
        width="1920"
        height="1080"
        :alt="data.entry.title"
        fetchpriority="high"
        class="absolute inset-0 w-full h-full object-cover"
      />

      <div class="absolute inset-x-0 bottom-0 mb-100 w-full z-10">
        <div class="container">
          <h1
            v-if="data?.entry?.heading"
            class="wysiwyg-title text-5xl md:text-6xl xl:text-7xl text-white max-w-screen-lg"
            v-html="data.entry.heading"
          />
          <NuxtLink
            v-if="data?.entry?.button?.url"
            :to="data.entry.button.url"
            :target="data.entry.button.target"
            class="inline-flex items-center text-2xl font-serif underline space-x-5 group text-white mt-25"
          >
            <span>{{ data.entry.button.text }}</span>
            <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="my-100">
      <div class="container text-secondary">
        <div class="flex flex-wrap justify-between">
          <h2
            v-if="data?.entry?.heading8"
            class="wysiwyg-title text-3xl md:text-4xl mr-20"
            v-html="data.entry.heading8"
          />
          <NuxtLink
            v-if="data?.brandsOverview?.uri"
            :to="`/${data.brandsOverview.uri}`"
            class="inline-flex items-center text-xl font-serif underline space-x-5 group"
          >
            <span>{{ $t('home.view-all-brands') }}</span>
            <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
          </NuxtLink>
        </div>

        <div
          v-if="data?.brands?.length"
          class="scrollbar overflow-x-auto w-full pb-20"
        >
          <ul class="flex items-stretch mt-20 space-x-20 text-xl text-secondary font-bold text-center">
            <li
              v-for="brand in data?.brands"
              :key="`brand-${brand.id}`"
            >
              <NuxtLink
                :to="`/${brand.uri}`"
                class="group flex items-center justify-center p-40 bg-primary-100 h-full min-w-[200px]"
                :aria-label="brand.title"
              >
                <img
                  v-if="brand.logo?.length"
                  :src="brand.logo[0].url"
                  :alt="brand.title"
                  width="328"
                  height="124"
                  class="mix-blend-darken grayscale group-hover:grayscale-0 transition"
                  loading="lazy"
                >
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section
      v-if="data?.entry?.heading4 || data?.entry?.totalConceptsHeading"
      class="my-75"
    >
      <div class="container text-secondary">
        <div class="grid gap-25 lg:grid-cols-2">
          <div
            v-if="data?.entry?.heading4"
            class="bg-primary-500 text-white"
          >
            <Media
              v-if="data?.entry?.image2?.length"
              :media="data.entry.image2[0]"
              sizes="(max-width: 600px) 100px, 50vw"
              :alt="data.entry.heading4"
              width="850"
              height="480"
              class="w-full aspect-[85/48] object-cover"
              loading="lazy"
            />
            <div class="p-25 md:p-40">
              <h3
                v-if="data?.entry?.heading4"
                class="wysiwyg-title text-3xl md:text-4xl lg:text-5xl mb-20"
                v-html="data.entry.heading4"
              />
              <div
                v-if="data?.entry?.intro"
                class="wysiwyg"
                v-html="data.entry.intro"
              />
              <div
                v-if="data?.entry?.button2?.url || data?.entry?.button4?.url"
                class="flex flex-wrap gap-x-25 gap-y-15 mt-20"
              >
                <NuxtLink
                  v-if="data.entry.button2?.url"
                  :to="data.entry.button2.url"
                  :target="data.entry.button2.target"
                  class="inline-flex items-center text-xl font-serif underline space-x-5 group"
                >
                  <span>{{ data.entry.button2.text }}</span>
                  <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
                </NuxtLink>
                <NuxtLink
                  v-if="data.entry.button4?.url"
                  :to="data.entry.button4.url"
                  :target="data.entry.button4.target"
                  class="inline-flex items-center text-xl font-serif underline space-x-5 group"
                >
                  <span>{{ data.entry.button4.text }}</span>
                  <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
                </NuxtLink>
              </div>
            </div>
          </div>

          <div
            v-if="data?.entry?.totalConceptsHeading"
            class="bg-primary-100 text-primary-700"
          >
            <Media
              v-if="data?.entry?.totalConceptsImage?.length"
              :media="data.entry.totalConceptsImage[0]"
              sizes="(max-width: 600px) 100px, 50vw"
              :alt="data.entry.totalConceptsHeading"
              width="850"
              height="480"
              class="w-full aspect-[85/48] object-cover"
              loading="lazy"
            />
            <div class="p-25 md:p-40">
              <h3
                v-if="data.entry.totalConceptsHeading"
                class="wysiwyg-title text-3xl md:text-4xl lg:text-5xl mb-20"
                v-html="data.entry.totalConceptsHeading"
              />
              <div
                v-if="data.entry.totalConceptsText"
                class="wysiwyg"
                v-html="data.entry.totalConceptsText"
              />
              <NuxtLink
                v-if="data.entry.totalConceptsButton?.url"
                :to="data.entry.totalConceptsButton.url"
                :target="data.entry.totalConceptsButton.target"
                class="inline-flex items-center text-xl font-serif underline space-x-5 group mt-20 text-secondary"
              >
                <span>{{ data.entry.totalConceptsButton.text }}</span>
                <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="my-100">
      <div class="container text-center">
        <h2
          v-if="data?.entry?.heading2"
          class="wysiwyg-title text-3xl md:text-4xl text-secondary"
          v-html="data.entry.heading2"
        />

        <div
          v-if="furniture.length"
          class="-mx-15"
        >
          <div class="scrollbar overflow-x-auto w-full pb-20">
            <ul class="flex items-end justify-around mt-20 text-xl text-secondary font-bold text-center min-w-[1200px]">
              <li
                v-for="{ id, icon } in furnitureCategories"
                :key="id"
              >
                <NuxtLink
                  :to="`/${furniture[id].uri}/`"
                  class="hover:text-primary-700 hover:underline transition lowercase"
                >
                  <component
                    :is="icon"
                    class="mx-auto"
                  />
                  <span class="inline-block pt-10">{{ furniture[id].title }}</span>
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <products-slider
      :heading="data?.entry?.heading3"
      :products="data?.entry?.products"
      :gtm-data="{
        item_list_id: 'favorite_products',
        item_list_name: 'Favorite products',
      }"
    />

    <section
      v-if="data?.roomsEntries?.length"
      class="my-100"
    >
      <div class="container px-0 xl:px-25 text-center">
        <h3
          v-if="data?.entry?.heading5"
          class="wysiwyg-title text-3xl md:text-4xl text-secondary"
          v-html="data.entry.heading5"
        />
        <div class="scrollbar overflow-x-auto w-full pb-20 text-left">
          <ul class="flex flex-nowrap mt-20 text-xl text-white font-bold">
            <li
              v-for="room in data?.roomsEntries"
              :key="`room-${room.id}`"
              class="flex-1 min-w-[300px]"
            >
              <NuxtLink
                :to="`/${room.uri}`"
                class="relative group"
              >
                <h4 class="absolute z-10 bottom-0 left-0 m-20 group-hover:underline">
                  {{ room.title }}
                </h4>
                <div class="relative overflow-hidden">
                  <Media
                    v-if="room.image?.length"
                    :media="room.image[0]"
                    :srcset="room.image[0].srcset"
                    sizes="(max-width: 600px) 100px, 100vw"
                    :alt="room.title"
                    width="495"
                    height="568"
                    class="w-full group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section
      v-if="data?.interiorStylesEntries?.length"
      class="my-100"
    >
      <div class="container px-0 xl:px-25 text-center">
        <h3
          v-if="data?.interiorStylesOverview?.heading2"
          class="wysiwyg-title text-3xl md:text-4xl text-secondary"
          v-html="data.interiorStylesOverview.heading2"
        />
        <div
          v-if="data?.interiorStylesOverview?.intro2"
          class="wysiwyg max-w-screen-md mx-auto mt-20 px-15 xl:px-0"
          v-html="data.interiorStylesOverview.intro2"
        />
        <div class="scrollbar overflow-x-auto w-full pb-20 text-left">
          <ul class="flex flex-nowrap mt-20 text-xl text-white font-bold">
            <li
              v-for="style in data.interiorStylesEntries"
              :key="`style-${style.id}`"
              class="flex-1 min-w-[300px]"
            >
              <NuxtLink
                :to="`/${style.uri}`"
                class="relative group"
              >
                <h4 class="absolute z-10 bottom-0 left-0 m-20 group-hover:underline">
                  {{ style.title }}
                </h4>
                <div class="relative overflow-hidden">
                  <Media
                    v-if="style.image?.length"
                    :media="style.image[0]"
                    :srcset="style.image[0].srcset"
                    sizes="(max-width: 600px) 100px, 100vw"
                    :alt="style.title"
                    width="495"
                    height="568"
                    class="w-full group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </NuxtLink>
            </li>
          </ul>
        </div>
        <NuxtLink
          v-if="data?.interiorStylesOverview?.uri"
          :to="`/${data.interiorStylesOverview.uri}`"
          class="inline-flex items-center text-xl font-serif underline space-x-5 group mt-25"
        >
          <span>{{ $t('home.view-all-interior-styles') }}</span>
          <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
        </NuxtLink>
      </div>
    </section>

    <section
      v-if="data?.entry?.configuratorImage?.length || data?.entry?.configuratorHeading || data?.entry?.configuratorText || data?.entry?.image3?.length || data?.entry?.heading6 || data?.entry?.intro2"
      class="my-100"
    >
      <div class="container lg:px-0 xl:px-25">
        <div class="grid gap-25 lg:grid-cols-2">
          <div class="bg-primary-100 text-primary-700">
            <Media
              v-if="data?.entry?.configuratorImage?.length"
              :media="data.entry.configuratorImage[0]"
              sizes="(max-width: 600px) 100px, 50vw"
              :alt="data.entry.configuratorHeading"
              width="850"
              height="480"
              class="w-full aspect-[85/48] object-cover"
              loading="lazy"
            />
            <div class="p-25 md:p-40">
              <h3
                v-if="data?.entry?.configuratorHeading"
                class="wysiwyg-title text-3xl md:text-4xl lg:text-5xl mb-20"
                v-html="data.entry.configuratorHeading"
              />
              <div
                v-if="data?.entry?.configuratorText"
                class="wysiwyg"
                v-html="data.entry.configuratorText"
              />
              <lightgallery
                :settings="{
                  licenseKey: '3E159A55-D6DA41F8-8FB9729E-B5490B88',
                  selector: 'a[href]',
                  download: false,
                  counter: false,
                }"
              >
                <a
                  href="https://jouweigentafel.nl/kotter"
                  :data-iframe="true"
                  :title="$t('nav.table-configurator')"
                  class="inline-flex items-center text-xl font-serif underline space-x-5 group mt-20 text-secondary"
                >
                  <span>{{ $t('nav.table-configurator') }}</span>
                  <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
                </a>
              </lightgallery>
            </div>
          </div>

          <div class="bg-primary-500 text-white">
            <Media
              v-if="data?.entry?.image3?.length"
              :media="data.entry.image3[0]"
              sizes="(max-width: 600px) 100px, 50vw"
              :alt="data.entry.heading6"
              width="850"
              height="480"
              class="w-full aspect-[85/48] object-cover"
              loading="lazy"
            />
            <div class="p-25 md:p-40">
              <h3
                v-if="data?.entry?.heading6"
                class="wysiwyg-title text-3xl md:text-4xl lg:text-5xl mb-20"
                v-html="data.entry.heading6"
              />
              <div
                v-if="data?.entry?.intro2"
                class="wysiwyg"
                v-html="data.entry.intro2"
              />
              <NuxtLink
                v-if="data?.entry?.button3?.url"
                :to="data.entry.button3.url"
                :target="data.entry.button3.target"
                class="inline-flex items-center text-xl font-serif underline space-x-5 group mt-20"
              >
                <span>{{ data.entry.button3.text }}</span>
                <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </section>

    <products-slider
      v-if="data?.entry?.inspirationEntry?.length"
      :heading="data.entry.heading7"
      :button="{ url: `/${data.entry.inspirationEntry[0]?.uri}`, text: $t('inspiration.cta') }"
      :products="data.entry.inspirationEntry[0]?.products"
      :gtm-data="{
        item_list_id: 'inspirational_products',
        item_list_name: 'Inspirational products',
      }"
    />

    <section
      v-if="instagram?.length"
      class="container my-50 lg:my-100"
    >
      <div
        v-if="data?.entry?.instagramText || data?.entry?.instagramHeading"
        class="grid grid-cols-2 md:grid-cols-6 gap-15 lg:gap-25"
      >
        <div class="col-start-1 md:col-end-4">
          <h3
            v-if="data?.entry?.instagramHeading"
            class="wysiwyg-title text-3xl md:text-4xl text-secondary mb-10"
            v-html="data.entry.instagramHeading"
          />
          <div
            v-if="data.entry?.instagramText"
            class="wysiwyg"
            v-html="data.entry.instagramText"
          />
        </div>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-6 md:grid-rows-4 gap-15 lg:gap-25">
        <div
          v-for="(item, key) in instagram"
          :key="item.id"
          :class="instagramCols[key]"
          class="relative aspect-square"
        >
          <NuxtLink
            :to="item.permalink"
            target="_blank"
            aria-label="View on Instagram"
            class="block size-full group"
          >
            <div class="pointer-events-none absolute inset-0 bg-black/30 text-white flex items-center justify-center z-1 group-hover:opacity-100 opacity-0 transition">
              <instagramIcon />
            </div>
            <img
              :src="item.thumbnail_url ?? item.media_url"
              loading="lazy"
              class="absolute object-cover top-0 left-0 size-full"
              alt=""
            >
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="my-100">
      <div class="container text-secondary text-center">
        <div class="max-w-screen-md mx-auto">
          <h4
            v-if="data?.entry?.heading9"
            class="wysiwyg-title text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-20"
            v-html="data.entry.heading9"
          />
          <div
            v-if="data?.entry?.intro3"
            class="text-primary-700 wysiwyg"
            v-html="data.entry.intro3"
          />

          <message :message="apiMessage" />

          <form @submit.prevent="subscribe">
            <label
              class="flex flex-col sm:flex-row space-x-20 items-center mt-25"
              for="email"
            >
              <input
                id="email"
                type="email"
                name="email"
                :placeholder="$t('newsletter.your-email')"
                class="flex w-full border-b border-primary-300 placeholder-primary-300 text-primary-700 focus:border-primary-700 transition outline-none"
              >
              <button
                type="submit"
                class="font-serif text-xl underline hover:text-primary-700 transition mt-20 sm:mt-0"
              >
                {{ $t('general.subscribe') }}
              </button>
            </label>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import Lightgallery from 'lightgallery/vue/LightGalleryVue.common.js';
import {
  definePageMeta,
  useI18n,
  useAsyncGql,
  watch,
  useSeo,
  useState,
  $fetch,
  onMounted,
} from '#imports';

// General icons
import chevronIcon from '@/assets/img/icon-chevron.svg';
import instagramIcon from '@/assets/img/icon-instagram.svg';

// Furniture icons
import accessoryIcon from '@/assets/img/icon-accessory.svg';
import chairIcon from '@/assets/img/icon-chair.svg';
import closetIcon from '@/assets/img/icon-closet.svg';
import couchIcon from '@/assets/img/icon-couch.svg';
import tableIcon from '@/assets/img/icon-table.svg';
import carpetIcon from '@/assets/img/icon-carpet.svg';
import lightingIcon from '@/assets/img/icon-lighting.svg';

// Data
const { locale } = useI18n({ useScope: 'global' });
const { data } = await useAsyncGql('home', {
  site: locale.value,
});
const apiMessage = useState('apiMessage', () => null);
const instagram = useState('instagram', () => []);
const instagramLimit = 7;
const instagramCols = {
  0: 'md:col-start-1 md:col-end-2 md:row-start-2',
  1: 'md:col-start-2 md:col-end-3 md:row-start-2',
  2: 'col-start-1 col-end-3 md:col-start-1 md:col-end-3 md:row-start-3 md:row-span-3',
  3: 'col-start-1 col-end-3 md:col-start-3 md:col-end-5 md:row-start-2 md:row-span-3',
  4: 'md:col-start-5 md:col-end-7 md:row-start-1 md:row-span-3',
  5: 'md:col-start-5 md:col-end-6 md:row-start-3',
  6: 'hidden md:block md:col-start-6 md:col-end-7 md:row-start-3',
};

// Custom data
const furniture = Object.fromEntries(data.value?.furniture?.map((f) => [f.handle, f]) ?? []);
const furnitureCategories = [
  { id: 'sofas', icon: couchIcon },
  { id: 'closets', icon: closetIcon },
  { id: 'chairs', icon: chairIcon },
  { id: 'tables', icon: tableIcon },
  { id: 'rugs', icon: carpetIcon },
  { id: 'lighting', icon: lightingIcon },
  { id: 'accessories', icon: accessoryIcon },
];

// Watcher
watch(data, (value) => {
  if (value?.entry?.seomatic) {
    useSeo(value.entry.seomatic);
  }
}, { immediate: true });

// Methods
/**
 * Submits subscribe form
 *
 * @param   {Event}  e
 */
const subscribe = async (e) => {
  apiMessage.value = null;
  const body = new FormData(e.target);

  try {
    await $fetch('/actions/nuxt/subscribe/subscribe', {
      params: { site: locale.value },
      method: 'POST',
      body,
    });

    apiMessage.value = {
      error: false,
      message: 'Inschrijving gelukt!',
    };
  } catch (error) {
    apiMessage.value = {
      error: true,
      ...error.data,
    };
  }
};

// Meta
definePageMeta({
  headerImage: true,
});

// Lifecycle hooks
onMounted(async () => {
  const { data: posts } = await $fetch('/actions/craftagram/default/api', {
    params: { limit: instagramLimit },
  });
  instagram.value = posts;
});
</script>
